/**
 * Control-bar colours per environment, and the contrast check behind V2-042 (COL-07).
 *
 * Every icon on the bar uses the bar text colour (white, 85–100%), and the current-page marker is
 * red on the Production slate but white on the coloured bars, where red-600 would vanish (it *is*
 * the Training colour, and reads about 1.3:1 on Development blue). The test checks all of them
 * reach at least 3:1 on every bar colour.
 */

export type ReleaseStage = 'development' | 'testing' | 'staging' | 'training' | 'production';

export const NAVBAR_BG: Record<ReleaseStage, string> = {
  development: '#2563eb', // tw-blue-600
  testing: '#166534', // tw-green-800
  staging: '#7e22ce', // tw-purple-700
  training: '#dc2626', // tw-red-600
  production: '#0f172a', // ink
};

/** The current-page underline (tailwind.css `.wrap-nav-parent-active`). */
export const CURRENT_MARKER: Record<ReleaseStage, string> = {
  development: '#ffffff',
  testing: '#ffffff',
  staging: '#ffffff',
  training: '#ffffff',
  production: '#dc2626',
};

const channel = (c: number) => {
  const s = c / 255;
  return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
};

const rgb = (hex: string): [number, number, number] => {
  const h = hex.replace('#', '');
  return [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16)) as [number, number, number];
};

const luminance = ([r, g, b]: [number, number, number]) =>
  0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);

/** WCAG contrast of `fg` drawn at `alpha` over `bg`. */
export function contrastRatio(fg: string, bg: string, alpha = 1): number {
  const f = rgb(fg);
  const b = rgb(bg);
  const blended = f.map((c, i) => c * alpha + b[i] * (1 - alpha)) as [number, number, number];
  const [hi, lo] = [luminance(blended), luminance(b)].sort((x, y) => y - x);
  return (hi + 0.05) / (lo + 0.05);
}
